"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
const servicesData = [{
  id: "item-1",
  title: "Personalized Daily Living Support",
  description: "Our compassionate team provides dedicated assistance with daily living activities (ADLs), including bathing, dressing, and mobility support, ensuring every resident maintains their dignity while receiving the precise level of care they need.",
  image: "https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/U2_bedroom-scaled.jpg?v=fh1rcuvj5zo"
}, {
  id: "item-2",
  title: "Cozy Private Rooms & Home Comforts",
  description: "Residents enjoy their own private sanctuaries, thoughtfully designed with comfortable furnishings and warm, sun-drenched aesthetics that feel like a high-end home, providing a safe and personal retreat.",
  image: "https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3577-scaled.jpg?v=tle2wag85q"
}, {
  id: "item-3",
  title: "Fresh, Family-Style Home-Cooked Meals",
  description: "We believe in the power of a shared meal. Our home-cooked dining experiences are tailored to individual dietary needs, featuring nutritious, delicious food prepared fresh daily in our family kitchen.",
  image: "https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3657-1-scaled.jpg?v=3n5a9i3c06h"
}, {
  id: "item-4",
  title: "Attentive 24/7 Home Supervision",
  description: "Peace of mind is paramount. Our experienced caregivers are on-site around the clock, providing 24/7 supervision and comprehensive medication management to ensure health and safety at all times.",
  image: "https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3636-scaled.jpg?v=c4y87bb806j"
}, {
  id: "item-5",
  title: "Intimate Daily Social Engagement",
  description: "Fostering connection and joy, we organize daily social activities and companionship opportunities, keeping our residents engaged, active, and part of a vibrant, close-knit micro-community.",
  image: "https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3641-1-scaled.jpg?v=xklbmncls6r"
}, {
  id: "item-6",
  title: "Individualized Boutique Care Planning",
  description: "Every resident receives an individualized care plan developed in consultation with families and healthcare professionals, tailored to meet their specific physical, emotional, and social needs.",
  image: "https://optimal-muskrat.10web.cloud/wp-content/uploads/2026/03/IMG_3592-scaled.jpg?v=jde5l1q1o6a"
}];
const careHighlights = [{
  id: "h1",
  text: "Personalized Daily Living Support"
}, {
  id: "h2",
  text: "Fresh, Home-Cooked Meals"
}, {
  id: "h3",
  text: "Attentive 24/7 Home Supervision"
}, {
  id: "h4",
  text: "Individualized Care Planning"
}];
export default function PersonalizedCare() {
  const [activeItem, setActiveItem] = useState<string>(servicesData[0].id);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="personalized-care" className="bg-background py-20 lg:py-32 overflow-hidden" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className={cn("grid lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {}
          <div className="order-2 lg:order-1 relative h-[400px] sm:h-[500px] lg:h-[650px] w-full rounded-3xl overflow-hidden shadow-2xl shadow-shadow-color/10">
            {servicesData.map((service, index) => <div key={service.id} data-index={index} className={cn("absolute inset-0 transition-opacity duration-700 ease-in-out", activeItem === service.id ? "opacity-100 z-10" : "opacity-0 z-0")}>
                <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                {}
                <div className="absolute inset-0 bg-primary/5 mix-blend-multiply pointer-events-none" />
              </div>)}
          </div>

          {}
          <div className="order-1 lg:order-2 flex flex-col justify-center">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-foreground tracking-tight mb-4">Personalized Assisted Living in Silver Spring, MD</h2>
              <p className="text-lg text-muted-foreground max-w-xl">
                Our home serves up to 5 residents, providing personalized, 
                family-style care that ensures every individual receives the 
                focused, compassionate attention they deserve in a true residential setting.
              </p>
              <ul className="mt-6 space-y-3">
                {careHighlights.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3 text-muted-foreground">
                    <Check className="size-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-base">{item.text}</span>
                  </li>)}
              </ul>
            </div>

            <Accordion type="single" value={activeItem} onValueChange={value => {
            if (value) setActiveItem(value);
          }} className="w-full mb-10">
              {servicesData.map((service, index) => <AccordionItem key={service.id} value={service.id} data-index={index} className="border-border py-2">
                  <AccordionTrigger className="text-lg md:text-xl font-medium text-foreground hover:text-primary transition-colors text-left">
                    {service.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed pt-2 pb-6 pr-6">
                    {service.description}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <Link to="/ScheduleATourPage">Schedule a Personal Tour</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8">
                <Link to="/CareServicesPage">Explore Our Home Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}